import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowDown } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const HERO_IMG =
  "https://images.unsplash.com/photo-1776860150272-653efc74193c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBoZWF0JTIwcHVtcCUyMGV4dGVyaW9yJTIwaG9tZSUyMGVuZXJneXxlbnwwfHx8fDE3ODk2NjI5NTB8MA&ixlib=rb-4.1.0&q=85";

const LINES = [
  { text: "Votre chaudière gaz", accent: false },
  { text: "ou fioul a vécu.", accent: false },
  { text: "Passez à la pompe", accent: true },
  { text: "à chaleur air/eau.", accent: true },
];

const STATS = [
  ["10 800 €", "d'aides MaPrimeRénov' + CEE*"],
  ["15 000 €", "d'éco-PTZ à 0 %"],
  ["70 %", "d'économies de chauffage*"],
  ["100 %", "des démarches gérées"],
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1.22]);

  return (
    <section
      ref={ref}
      data-testid="hero"
      className="relative flex min-h-screen flex-col justify-end overflow-hidden"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Pompe à chaleur air/eau installée devant une maison"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-forest-deep/80 via-forest-deep/45 to-forest-deep" />
      <div className="absolute inset-0 bg-gradient-to-r from-forest-deep/85 via-forest-deep/30 to-transparent" />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-16 pt-32 sm:px-8 sm:pb-24">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="mb-6 font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-emerald-300 sm:text-xs"
        >
          Installateur RGE QualiPAC — Gironde · Charente · Charente-Maritime · Dordogne · Lot-et-Garonne · Landes
        </motion.p>

        <h1 className="font-display text-[clamp(2.6rem,7vw,5.6rem)] font-extrabold leading-[1.04] tracking-tight text-white">
          {LINES.map((line, i) => (
            <span key={i} className="mask-line">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{
                  duration: 0.9,
                  delay: 0.25 + i * 0.13,
                  ease: [0.16, 1, 0.3, 1],
                }}
                className={`block ${line.accent ? "text-emerald-300" : ""}`}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85 }}
          className="mt-7 max-w-2xl text-base leading-relaxed text-white/75 sm:text-lg"
        >
          Jusqu'à <strong className="text-white">10 800 € d'aides cumulées</strong>{" "}
          (MaPrimeRénov' + prime CEE), un prêt à taux zéro jusqu'à 15 000 € et
          jusqu'à 70 % d'économies sur votre chauffage. Nous gérons 100 % des
          démarches, du dossier d'aides à la mise en service.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-simulator"
            onClick={(e) => {
              e.preventDefault();
              scrollToId("#simulateur");
            }}
            className="group flex items-center gap-2 rounded-full bg-emerald-400 px-7 py-4 text-base font-bold text-forest-deep transition-all hover:scale-[1.03] hover:bg-emerald-300 hover:shadow-glow"
          >
            Tester mon éligibilité
            <ArrowDown className="h-4 w-4 transition-transform group-hover:translate-y-0.5" />
          </button>
          <a
            href="tel:+33535759803"
            data-testid="hero-cta-phone"
            className="flex items-center gap-2.5 rounded-full border border-white/25 px-7 py-4 text-base font-semibold text-white transition-all hover:border-emerald-300 hover:text-emerald-300"
          >
            <Phone className="h-4 w-4" /> 05 35 75 98 03
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="mt-14 grid grid-cols-2 gap-x-6 gap-y-8 border-t border-white/15 pt-8 sm:mt-20 lg:grid-cols-4"
        >
          {STATS.map(([value, label], i) => (
            <div key={i} data-testid={`hero-stat-${i}`}>
              <p className="font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
                {value}
              </p>
              <p className="mt-1.5 text-xs leading-relaxed text-white/55 sm:text-sm">
                {label}
              </p>
            </div>
          ))}
        </motion.div>
        <p className="mt-5 text-[11px] text-white/35">
          *Selon vos revenus et votre projet — barèmes 2026, estimation
          indicative.
        </p>
      </div>
    </section>
  );
}
